import {Dialog} from 'primereact/dialog';
import {Message} from 'primereact/message';
import {withTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import DataTableLazy from '../../components/DataTableLazy';
import AuthService from '../../services/AuthService';
import {FaTrophy} from 'react-icons/fa';
import CriteriaCalendarRangeComponent from '../../components/criteria/CriteriaCalendarRangeComponent';
import ImageSimpleComponent from '../../components/inputs/ImageSimpleComponent';
import StringUtils from '../../utils/StringUtils';
import InternalUserService from '../../services/MobileUserService';
import RankingService from '../../services/RankingService';
import {t} from 'i18next';
import CompanyService from '../../services/CompanyService';
import {
    getTeamPointsMembersInfoText,
    GROUP_POINTS_ALGORITHM,
    normalizeGroupPointsAlgorithm,
} from '../../utils/GroupPointsAlgorithmUtils';

const formerMemberStyle = (rowData) =>
    rowData?.assignedToGroup === false ? {color: '#9e9e9e', opacity: 0.75} : {};

export const GroupRankingMembersDialog = ({
    visible,
    group,
    initialDateFrom,
    initialDateTo,
    onHide,
    rows,
    rowsPerPageOptions,
    groupPointsAlgorithm: groupPointsAlgorithmProp,
}) => {
    const rankingService = new RankingService();
    const mobileUserService = new InternalUserService();
    const authService = new AuthService();

    const buildCriteria = (dateFrom, dateTo) => ({
        groupId: group?.groupId,
        dateFrom,
        dateTo,
        firstResult: 0,
        maxResults: rows || 20,
    });

    const [criteria, setCriteria] = useState(buildCriteria(initialDateFrom, initialDateTo));
    const [dateFrom, setDateFrom] = useState(initialDateFrom);
    const [dateTo, setDateTo] = useState(initialDateTo);
    const [membersPageable, setMembersPageable] = useState({content: [], totalElements: 0});
    const [groupPointsAlgorithm, setGroupPointsAlgorithm] = useState(
        normalizeGroupPointsAlgorithm(groupPointsAlgorithmProp)
    );

    useEffect(() => {
        setGroupPointsAlgorithm(normalizeGroupPointsAlgorithm(groupPointsAlgorithmProp));
    }, [groupPointsAlgorithmProp]);

    useEffect(() => {
        if (groupPointsAlgorithmProp) {
            return;
        }
        const companyService = new CompanyService();
        companyService
            .get()
            .then((data) => setGroupPointsAlgorithm(normalizeGroupPointsAlgorithm(data?.groupPointsAlgorithm)))
            .catch(() => setGroupPointsAlgorithm(GROUP_POINTS_ALGORITHM.LOGARITHM));
    }, [groupPointsAlgorithmProp]);

    const fetchMembers = (nextCriteria) => {
        if (!group?.groupId) {
            return Promise.resolve();
        }
        return rankingService.getGroupMemberRanking(group.groupId, nextCriteria).then((data) => {
            setMembersPageable(data);
            return data;
        });
    };

    useEffect(() => {
        if (visible && group?.groupId) {
            const nextCriteria = buildCriteria(initialDateFrom, initialDateTo);
            setDateFrom(initialDateFrom);
            setDateTo(initialDateTo);
            setCriteria(nextCriteria);
            fetchMembers(nextCriteria);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, group?.groupId, initialDateFrom, initialDateTo]);

    const rankTemplate = (rowData) => {
        const style = formerMemberStyle(rowData);
        if (rowData.lp === 1) {
            return <FaTrophy className='text-warning' style={{fontSize: '28px', ...style}} />;
        }
        return <div style={{fontSize: '16px', marginLeft: '9px', ...style}}>{rowData.lp}</div>;
    };

    const avatarTemplate = (rowData) => {
        const userId = rowData?.userId;
        if (StringUtils.isBlank(userId)) {
            return '';
        }
        const avatarPath = `${mobileUserService.getPath()}/${userId}/avatar/user`;
        return (
            <div style={formerMemberStyle(rowData)}>
                <ImageSimpleComponent
                    srcUrl={`${avatarPath}?token=${authService.getToken()}`}
                    borderRadius={50}
                    height={40}
                    width={40}
                />
            </div>
        );
    };

    const nameTemplate = (rowData) => (
        <span style={formerMemberStyle(rowData)}>
            {rowData.name}
            {rowData.assignedToGroup === false ? ` (${t('rankingPage.formerMember')})` : ''}
        </span>
    );

    const pointsTemplate = (rowData) => (
        <span style={{...formerMemberStyle(rowData), fontVariantNumeric: 'tabular-nums'}}>{rowData.earnedTotal}</span>
    );

    const cleanPaginationCriteria = (nextCriteria) => {
        nextCriteria.firstResult = 0;
        nextCriteria.maxResults = rows || 20;
    };

    return (
        <Dialog
            header={`${t('rankingPage.showPointEarnings')}: ${group?.name || ''}`}
            visible={visible}
            style={{width: '80vw', maxWidth: '900px'}}
            onHide={onHide}
            modal
            dismissableMask
        >
            <Message
                severity='info'
                className='w-100 mb-3'
                text={getTeamPointsMembersInfoText(t, groupPointsAlgorithm)}
            />
            <div className='row mb-3'>
                <div className='col-lg-6 offset-lg-6'>
                    <CriteriaCalendarRangeComponent
                        idFrom='group-members-date-from'
                        idTo='group-members-date-to'
                        nameFrom='dateFrom'
                        nameTo='dateTo'
                        from={dateFrom}
                        to={dateTo}
                        label={'Zakres'}
                        onChange={(type, names, e) => {
                            const value = e?.value;
                            const name = e?.target?.name;
                            const nextCriteria = {...criteria};
                            if (name === 'dateFrom') {
                                setDateFrom(value);
                                nextCriteria.dateFrom = value;
                            } else if (name === 'dateTo') {
                                setDateTo(value);
                                nextCriteria.dateTo = value;
                            }
                            cleanPaginationCriteria(nextCriteria);
                            setCriteria(nextCriteria);
                            fetchMembers(nextCriteria);
                        }}
                        sharedLabel={true}
                        inputsClassName='col-md-6'
                        colClass='col-lg-12'
                        showButtonBar={true}
                    />
                </div>
            </div>

            <DataTableLazy
                id='group-members-table'
                dataPageable={membersPageable}
                rendered={visible}
                filterDisplay={'menu'}
                fetchReturnResolve={true}
                rows={rows || 20}
                loadLazyData={(nextCriteria) => fetchMembers(nextCriteria)}
                paginator={true}
                onSelect={() => {}}
                rowsPerPageOptions={rowsPerPageOptions || [10, 20, 50]}
                initialCriteria={criteria}
                columns={[
                    {field: '', header: t('dashboard.place'), body: rankTemplate},
                    {field: 'avatarUuid', header: t('dashboard.avatar'), body: avatarTemplate},
                    {field: 'name', header: t('dashboard.user'), body: nameTemplate},
                    {
                        field: 'earnedTotal',
                        header: t('dashboard.points'),
                        body: pointsTemplate,
                        bodyStyle: {textAlign: 'center'},
                        style: {width: '120px'},
                    },
                ]}
            />
        </Dialog>
    );
};

GroupRankingMembersDialog.propTypes = {
    visible: PropTypes.bool,
    group: PropTypes.shape({
        groupId: PropTypes.number,
        name: PropTypes.string,
    }),
    initialDateFrom: PropTypes.instanceOf(Date),
    initialDateTo: PropTypes.instanceOf(Date),
    onHide: PropTypes.func.isRequired,
    rows: PropTypes.number,
    rowsPerPageOptions: PropTypes.array,
    groupPointsAlgorithm: PropTypes.string,
};

export default withTranslation()(GroupRankingMembersDialog);
